import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from '@arco-design/web-react';
import { adminCardsApi } from '@/api';
import { formatMoney } from '@/utils/money';
import type {
  AdminCardItem,
  AdminCardListQuery,
  CardStatus,
} from '@/types/card';
import type { PaginatedResult } from '@/types/api';

const STATUS_COLOR: Record<CardStatus, string> = {
  PENDING: 'blue',
  ACTIVE: 'green',
  INACTIVE: 'gray',
  FROZEN: 'red',
  CLOSING: 'orange',
  CLOSED: 'gray',
  REJECTED: 'red',
};

export default function CardsView() {
  const [data, setData] = useState<PaginatedResult<AdminCardItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState<AdminCardListQuery>({ page: 1, pageSize: 20 });

  const load = useCallback(() => {
    setLoading(true);
    adminCardsApi
      .list(query)
      .then(setData)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFreeze = (card: AdminCardItem, freeze: boolean) => {
    let reason = '';
    Modal.confirm({
      title: freeze ? `冻结卡 ${card.panMasked}` : `解冻卡 ${card.panMasked}`,
      content: (
        <div>
          <div style={{ marginBottom: 8, color: 'var(--admin-text-secondary)' }}>
            将同步到上游 Newgen 通道，并写入审计日志。请填写原因：
          </div>
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder={freeze ? '如：风控复检' : '如：客户申诉通过'}
            onChange={(v) => (reason = v)}
          />
        </div>
      ),
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        if (!reason.trim() || reason.trim().length < 4) {
          Message.warning('请填写至少 4 个字符的操作原因');
          throw new Error('reason required');
        }
        if (freeze) {
          await adminCardsApi.freeze({ cardId: card.id, reason: reason.trim() });
        } else {
          await adminCardsApi.unfreeze(card.id, reason.trim());
        }
        Message.success(freeze ? '冻结指令已下达' : '解冻指令已下达');
        load();
      },
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <div>
          <div className="admin-page__title">卡片管理</div>
          <div className="admin-page__subtitle">
            全平台虚拟卡列表 · 支持冻结 / 解冻（同步上游 + 审计）
          </div>
        </div>
      </div>

      <div className="admin-card">
        <Form
          layout="inline"
          style={{ marginBottom: 12 }}
          initialValues={query}
          onSubmit={(values) => setQuery({ ...query, ...values, page: 1 })}
        >
          <Form.Item field="keyword" label="搜索">
            <Input placeholder="客户名 / 卡末四位 / 上游卡 ID" allowClear style={{ width: 260 }} />
          </Form.Item>
          <Form.Item field="status" label="状态">
            <Select placeholder="全部" allowClear style={{ width: 140 }}>
              {(
                ['PENDING', 'ACTIVE', 'INACTIVE', 'FROZEN', 'CLOSING', 'CLOSED', 'REJECTED'] as CardStatus[]
              ).map((s) => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
              <Button onClick={() => setQuery({ page: 1, pageSize: 20 })}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<AdminCardItem>
          loading={loading}
          rowKey="id"
          data={data?.items ?? []}
          pagination={{
            current: query.page ?? 1,
            pageSize: query.pageSize ?? 20,
            total: data?.total ?? 0,
            onChange: (p) => setQuery({ ...query, page: p }),
            showTotal: true,
          }}
          columns={[
            { title: '所属客户', dataIndex: 'customerUsername' },
            {
              title: '卡号（脱敏）',
              dataIndex: 'panMasked',
              render: (v: string) => <span className="admin-mono">{v}</span>,
            },
            { title: '卡段 BIN', dataIndex: 'bin' },
            { title: '别名', dataIndex: 'alias', render: (v) => v || '-' },
            {
              title: '状态',
              dataIndex: 'status',
              render: (v: CardStatus) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
            },
            {
              title: '余额',
              dataIndex: 'balanceCents',
              align: 'right',
              render: (v: number) => formatMoney(v),
            },
            {
              title: '额度',
              dataIndex: 'limitCents',
              align: 'right',
              render: (v: number) => formatMoney(v),
            },
            { title: '创建时间', dataIndex: 'createdAt' },
            {
              title: '操作',
              dataIndex: 'op',
              width: 160,
              render: (_, row) => {
                if (row.status === 'FROZEN') {
                  return (
                    <Button size="mini" onClick={() => handleFreeze(row, false)}>
                      解冻
                    </Button>
                  );
                }
                if (['CLOSED', 'CLOSING', 'REJECTED'].includes(row.status)) {
                  return <span style={{ color: 'var(--admin-text-tertiary)' }}>不可操作</span>;
                }
                return (
                  <Button size="mini" status="danger" onClick={() => handleFreeze(row, true)}>
                    冻结
                  </Button>
                );
              },
            },
          ]}
        />
      </div>
    </div>
  );
}
